/*
 * Pinzelli — product-variants.js
 * Sélection de variantes, formulaire d'ajout au panier, quantité.
 * Vanilla JS / custom elements. Aucune dépendance externe obligatoire.
 * (Compatible avec global.js s'il est présent : utilise publish/subscribe si disponibles.)
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */
  var BUS = {
    publish: function (name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS[name]) {
        try { window.publish(window.PUB_SUB_EVENTS[name], detail); } catch (e) { /* noop */ }
      }
    }
  };

  function strings() {
    return window.variantStrings || {};
  }

  function formatMoney(cents, format) {
    var fmt = format || window.themeMoneyFormat || '{{amount_with_comma_separator}}€';
    var value = (cents / 100).toFixed(2);
    function withComma(v) { return v.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace(' ,', ','); }
    function withPeriod(v) { return v.replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withPeriod(value);
        case 'amount_no_decimals': return withPeriod((cents / 100).toFixed(0));
        case 'amount_with_comma_separator': return withComma(value);
        case 'amount_no_decimals_with_comma_separator': return withComma((cents / 100).toFixed(0));
        case 'amount_with_space_separator': return withComma(value);
        default: return withComma(value);
      }
    });
  }

  function fetchConfigJSON() {
    return {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }
    };
  }

  /* ------------------------------------------------------------------ *
   * <quantity-input> : boutons +/- accessibles
   * ------------------------------------------------------------------ */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.quantity__input');
        if (!this.input) return;
        this.changeEvent = new Event('change', { bubbles: true });
        this.querySelectorAll('button').forEach(function (btn) {
          btn.addEventListener('click', this.onButtonClick.bind(this));
        }, this);
        this.input.addEventListener('change', this.validate.bind(this));
        this.validate();
      }
      onButtonClick(event) {
        event.preventDefault();
        var previous = this.input.value;
        if (event.currentTarget.name === 'plus') this.input.stepUp();
        else this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(this.changeEvent);
        this.validate();
      }
      validate() {
        var value = parseInt(this.input.value, 10) || 1;
        var minus = this.querySelector('.quantity__button[name="minus"]');
        var plus = this.querySelector('.quantity__button[name="plus"]');
        if (minus && this.input.min) minus.classList.toggle('disabled', value <= parseInt(this.input.min, 10));
        if (plus && this.input.max) plus.classList.toggle('disabled', value >= parseInt(this.input.max, 10));
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * <product-variant-selects> : émet un évènement quand une option change
   * ------------------------------------------------------------------ */
  if (!customElements.get('product-variant-selects')) {
    customElements.define('product-variant-selects', class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onChange.bind(this));
        // Les liens de type "pastille" (galerie pic-) déclenchent aussi une sélection.
        this.addEventListener('click', this.onLinkClick.bind(this));
      }
      onLinkClick(event) {
        var link = event.target.closest('[data-option-link]');
        if (!link || !this.contains(link)) return;
        event.preventDefault();
        var input = this.querySelector('#' + CSS.escape(link.getAttribute('data-option-link')));
        if (input && !input.disabled) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      onChange(event) {
        if (event.target.tagName === 'SELECT' && event.type === 'change') this.syncSelectSelectedAttr(event.target);
        if (event.target.tagName === 'SELECT' && event.type === 'input') this.syncSelectSelectedAttr(event.target);
        this.updateSelectedLabels();
        this.dispatchEvent(new CustomEvent('variant-selects:change', {
          bubbles: true,
          detail: { selectedValues: this.selectedValues, optionValueIds: this.optionValueIds, target: event.target }
        }));
      }
      syncSelectSelectedAttr(select) {
        Array.prototype.forEach.call(select.options, function (opt) {
          if (opt.value === select.value) opt.setAttribute('selected', 'selected');
          else opt.removeAttribute('selected');
        });
      }
      updateSelectedLabels() {
        this.querySelectorAll('.product-form__input').forEach(function (field) {
          var display = field.querySelector('[data-selected-value]');
          if (!display) return;
          var checked = field.querySelector('input[type="radio"]:checked');
          if (checked) { display.textContent = checked.value; return; }
          var select = field.querySelector('select');
          if (select) display.textContent = select.value;
        });
      }
      /** Valeurs sélectionnées, indexées sur la position réelle de l'option. */
      get selectedValues() {
        var values = [];
        Array.prototype.forEach.call(this.querySelectorAll('[data-option-position]'), function (field) {
          var index = (parseInt(field.dataset.optionPosition, 10) || 1) - 1;
          var checked = field.querySelector('input[type="radio"]:checked');
          if (checked) { values[index] = checked.value; return; }
          var select = field.querySelector('select');
          if (select) values[index] = select.value;
        });
        return values;
      }
      get optionValueIds() {
        return Array.prototype.map.call(
          this.querySelectorAll('input[type="radio"]:checked, select option[selected]'),
          function (el) { return el.dataset.optionValueId; }
        ).filter(Boolean);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * <product-info> : chef d'orchestre
   * ------------------------------------------------------------------ */
  if (!customElements.get('product-info')) {
    customElements.define('product-info', class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.variants = this.readVariants();
        this.optionCount = this.variants.length ? 3 : 0;
        this.abortController = null;
        this.onSelectChange = this.handleOptionChange.bind(this);
        this.addEventListener('variant-selects:change', this.onSelectChange);
        this.updateAvailability();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }
      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
      }

      readVariants() {
        var node = this.querySelector('[data-product-variants-json]');
        if (!node) return [];
        try { return JSON.parse(node.textContent); } catch (e) { return []; }
      }

      get picker() { return this.querySelector('product-variant-selects'); }
      get pickers() { return Array.prototype.slice.call(this.querySelectorAll('product-variant-selects')); }
      get form() { return this.querySelector('product-form'); }
      get gallery() { return this.querySelector('product-gallery'); }

      /** Trouve la variante correspondant exactement aux valeurs sélectionnées. */
      findVariant(values) {
        return this.variants.find(function (variant) {
          for (var i = 0; i < values.length; i++) {
            if (values[i] === null || values[i] === undefined) continue;
            if (variant.options[i] !== values[i]) return false;
          }
          return true;
        }) || null;
      }

      /** Grise / barre les valeurs d'option impossibles ou en rupture. */
      updateAvailability() {
        if (!this.variants.length) return;
        this.pickers.forEach(this.updatePickerAvailability, this);
      }

      updatePickerAvailability(picker) {
        var selected = picker.selectedValues;
        var variants = this.variants;

        picker.querySelectorAll('[data-option-position]').forEach(function (field) {
          var position = parseInt(field.dataset.optionPosition, 10) - 1;
          var inputs = field.querySelectorAll('input[type="radio"], option');
          Array.prototype.forEach.call(inputs, function (input) {
            var candidate = selected.slice();
            candidate[position] = input.value;
            var match = variants.filter(function (variant) {
              for (var i = 0; i < candidate.length; i++) {
                if (candidate[i] === null || candidate[i] === undefined) continue;
                if (variant.options[i] !== candidate[i]) return false;
              }
              return true;
            });
            var exists = match.length > 0;
            var available = match.some(function (v) { return v.available; });
            var label = input.tagName === 'OPTION' ? null : field.querySelector('label[for="' + input.id + '"]');
            if (input.tagName === 'OPTION') {
              input.disabled = !exists;
              input.textContent = input.dataset.optionLabel + (available ? '' : ' — ' + (strings().unavailable || 'Indisponible'));
            } else {
              input.classList.toggle('visually-disabled', !available);
              input.classList.toggle('disabled', !exists);
              input.disabled = !exists;
              if (label) {
                label.classList.toggle('product-option__label--unavailable', !available);
                label.setAttribute('aria-disabled', String(!available));
              }
            }
          });
        });
      }

      handleOptionChange(event) {
        var picker = event.target.closest('product-variant-selects') || this.picker;
        if (!picker) return;
        var values = picker.selectedValues;
        var variant = this.findVariant(values);
        // Aligne les autres sélecteurs présents (encart pic- + picker classique).
        this.pickers.forEach(function (other) {
          if (other === picker) return;
          other.querySelectorAll('[data-option-position]').forEach(function (field) {
            var position = parseInt(field.dataset.optionPosition, 10) - 1;
            var value = values[position];
            if (value === null || value === undefined) return;
            var input = Array.prototype.find.call(
              field.querySelectorAll('input[type="radio"]'),
              function (radio) { return radio.value === value; }
            );
            if (input) input.checked = true;
            var select = field.querySelector('select');
            if (select) select.value = value;
          });
          other.updateSelectedLabels();
        });
        this.updateAvailability();
        this.applyVariant(variant);
        this.refreshSection(variant);
      }

      /** Mise à jour optimiste, sans requête réseau. */
      applyVariant(variant) {
        this.updateUrl(variant);
        this.updateFormInputs(variant);
        this.updateSubmitButton(variant);
        this.updateMedia(variant);
        BUS.publish('variant:change', { sectionId: this.sectionId, variant: variant });
      }

      updateUrl(variant) {
        if (this.dataset.updateUrl === 'false') return;
        var url = this.productUrl + (variant ? '?variant=' + variant.id : '');
        window.history.replaceState({}, '', url);
        var share = this.querySelector('[data-share-url]');
        if (share) {
          var absolute = (window.shopUrl || window.location.origin) + url;
          share.setAttribute('data-share-url', absolute);
          var field = share.querySelector('input');
          if (field) field.value = absolute;
        }
      }

      updateFormInputs(variant) {
        this.querySelectorAll('input[name="id"]').forEach(function (input) {
          input.value = variant ? variant.id : '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateSubmitButton(variant) {
        var s = strings();
        this.querySelectorAll('[data-add-to-cart]').forEach(function (button) {
          var text = button.querySelector('[data-add-to-cart-text]') || button.querySelector('span');
          if (!variant) {
            button.setAttribute('disabled', 'disabled');
            if (text) text.textContent = s.unavailable || 'Indisponible';
          } else if (!variant.available) {
            button.setAttribute('disabled', 'disabled');
            if (text) text.textContent = s.soldOut || 'Épuisé';
          } else {
            button.removeAttribute('disabled');
            if (text) {
              var base = s.addToCart || 'Ajouter au panier';
              text.textContent = button.dataset.showPrice === 'true'
                ? base + ' · ' + formatMoney(variant.price, button.dataset.moneyFormat)
                : base;
            }
          }
        });
      }

      updateMedia(variant) {
        if (!variant) return;
        var mediaId = variant.featured_media ? variant.featured_media.id : null;
        if (!mediaId) return;
        var gallery = this.gallery;
        if (gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(mediaId);
      }

      /**
       * Section Rendering API : recharge proprement le contenu du bloc produit
       * (prix, stock, SKU, encarts, sticky ATC) pour la variante choisie.
       */
      refreshSection(variant) {
        if (this.dataset.sectionRendering === 'false') return;
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        var url = this.productUrl + '?section_id=' + encodeURIComponent(this.sectionId)
          + (variant ? '&variant=' + variant.id : '');
        var self = this;
        this.setAttribute('data-loading', 'true');
        fetch(url, { signal: this.abortController.signal })
          .then(function (response) { return response.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            self.swapRegion(doc, 'ProductInfo-' + self.sectionId);
            self.swapRegion(doc, 'ProductStickyCart-' + self.sectionId);
            self.updateAvailability();
            BUS.publish('variant:rendered', { sectionId: self.sectionId, variant: variant });
          })
          .catch(function (error) { if (error.name !== 'AbortError') console.error(error); })
          .finally(function () { self.removeAttribute('data-loading'); });
      }

      /**
       * Remplace une zone en préservant le picker (pour ne pas perdre le focus clavier).
       */
      swapRegion(doc, id) {
        var source = doc.getElementById(id);
        var target = this.querySelector('#' + CSS.escape(id));
        if (!source || !target) return;
        var focusedId = document.activeElement ? document.activeElement.id : null;
        var openPicker = target.querySelector('product-variant-selects');
        var freshPicker = source.querySelector('product-variant-selects');
        target.innerHTML = source.innerHTML;
        if (openPicker && freshPicker) {
          // Rien à faire : le nouveau picker porte déjà la bonne sélection.
        }
        if (focusedId) {
          var again = this.querySelector('#' + CSS.escape(focusedId));
          if (again && typeof again.focus === 'function') again.focus({ preventScroll: true });
        }
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * <product-form> : ajout au panier en AJAX
   * ------------------------------------------------------------------ */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.submitButton = this.querySelector('[type="submit"]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        var idInput = this.form.querySelector('[name="id"]');
        if (idInput) idInput.disabled = false;
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer') || document.querySelector('quick-cart-drawer') || document.querySelector('cart-notification');
      }

      onSubmit(event) {
        if (!window.routes || !window.routes.cart_add_url) return; // fallback : POST natif
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.showError(false);
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        var spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        var formData = new FormData(this.form);
        var drawer = this.cartDrawer;
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          formData.append('sections', drawer.getSectionsToRender().map(function (s) { return s.section; }).join(','));
          formData.append('sections_url', window.location.pathname);
        }
        var config = fetchConfigJSON();
        config.body = formData;

        var self = this;
        fetch(window.routes.cart_add_url, config)
          .then(function (response) { return response.json(); })
          .then(function (response) {
            if (response.status) {
              self.showError(response.description || response.message);
              BUS.publish('cart:error', { source: 'product-form', errors: response.errors, message: response.message });
              return;
            }
            BUS.publish('cart:update', { source: 'product-form', cartData: response });
            if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(response);
            else if (drawer && typeof drawer.open === 'function') drawer.open();
            else window.location = window.routes.cart_url || '/cart';
          })
          .catch(function (error) { console.error(error); })
          .finally(function () {
            self.submitButton.classList.remove('loading');
            self.submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      showError(message) {
        if (this.hideErrors || !this.errorWrapper) return;
        this.errorWrapper.toggleAttribute('hidden', !message);
        if (message && this.errorMessage) this.errorMessage.textContent = message;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * <product-sticky-cart> : barre d'ajout collante
   * ------------------------------------------------------------------ */
  if (!customElements.get('product-sticky-cart')) {
    customElements.define('product-sticky-cart', class extends HTMLElement {
      connectedCallback() {
        var anchorSelector = this.dataset.anchor;
        this.anchor = anchorSelector ? document.querySelector(anchorSelector) : null;
        if (!this.anchor || !('IntersectionObserver' in window)) return;
        this.lastY = window.scrollY;
        var self = this;
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var goingDown = window.scrollY > self.lastY;
            self.lastY = window.scrollY;
            if (window.Shopify && window.Shopify.designMode) { self.classList.add('is-hidden'); return; }
            if (goingDown && !entry.isIntersecting) self.classList.remove('is-hidden');
            else if (entry.isIntersecting) self.classList.add('is-hidden');
          });
        }, { threshold: 0.6 });
        this.observer.observe(this.anchor);
      }
      disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
    });
  }
})();
